/**
 * Created by Tom.Jaeschke on 6/8/2017.
 */
import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, Observable, Subject } from 'rxjs';
import {
  PerDiemFile,
  PerDiemRuleDto,
  PerDiemViewpointSideUpdate,
  PerDiemSpartaSideUpdate,
} from '@ups/xplat/api/dto';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';
import { apiErrorMessage, parseData } from '@ups/xplat/utils';
import { map } from 'rxjs/operators';

const GET_PER_DIEM_SUPPORTED_TYPES = '/api/sparta/perdiemdocs/supportedTypes';
const GET_PER_DIEM_RULE_FOR_JOB =
  '/api/manPowerReq/{manPowerReqID}/perDiemRule';
const GET_PER_DIEM_RULE_FOR_DETAIL =
  '/api/manPowerReqDetail/{manPowerReqDetailID}/perDiemRule';
const GET_EXPIRED_PER_DIEM = '/api/e2e/perdiemfiles/expired/';
const GET_UNEXPIRED_PER_DIEM = '/api/e2e/perdiemfiles/unexpired/'; //Guid? employeeJobID, Guid? manPowerReqDetailID
const GET_ALL_PER_DIEM = '/api/e2e/perdiemfiles/all/';
const SAVE_PER_DIEM_FILE = '/api/e2e/spartasideupload/'; //Guid? employeeJobID
const UPDATE_PER_DIEM_SPARTA = '/api/e2e/spartasideupdate/';
const UPDATE_PER_DIEM_VIEWPOINT = '/api/sparta/employeeportalperdiemdocs/';
const DOWNLOAD_FROM_VIEWPOINT = '/api/e2e/pullviewpointfilethroughsparta/';
const DOWNLOAD_FROM_SPARTA = '/api/e2e/pullazurefile/';
const ALTER_PER_DIEM_ASSOCIATION = '/api/e2e/alterperdiemassociation/';
const CREATE_SPARTA_SIDE_RECORD_WITH_PER_DIEM_FILE_ASSOCIATION =
  '/api/e2e/createspartasiderecordwithperdiemfileassociation/';
const CREATE_SPARTA_SIDE_RECORD_WITHOUT_PER_DIEM_FILE_ASSOCIATION =
  '/api/e2e/createspartasiderecordwithoutperdiemfileassociation/';
const GET_PERDIEM_DATA = '/api/perDiem/audit';
const GET_ALL_PERDIEM_DATA = '/api/perDiem/audit/job/all';
const GET_PERDIEM_ALL_JOBS_DATA = '/api/jobs/all';
const GET_PERDIEM_JOBS_DATA = '/api/jobs';
const GET_SELECTED_PERDIEM_JOBS_DATA = '/api/perDiem/audit/job/{jobId}';
const GET_SELECTED_PERDIEM_JOBS_ODATA = '/api/perDiem/audit-odata/job/{jobId}';

const GET_DATA_FOR_PERDIEM_POPUP =
  '/api/perDiem/filesForApproval/{employeeJobId}';
const GET_FILE_DATA_FOR_PERDIEM_POPUP = '/api/perDiem/downloadfile/{fileID}';
const ACCEPT_PERDIEM_FILE = '/api/perDiem/accept/file/{perDiemFileID}';
const REJECT_PERDIEM_FILE = '/api/perDiem/reject/file/{perDiemFileID}';
const GENERATE_PERDIEM_PACKETS_FOR_JOB = '/api/e2e/perdiempacket/job/{jobId}';
const GENERATE_PERDIEM_PACKETS_FOR_EMPLOYEES =
  '/api/e2e/perdiempacket/employees';

interface PerDiemAssociationResult {
  FileID: string;
  PerDiemFileID: string;
}

interface SpartaFileLinkResult {
  Data: string;
}

interface ViewpointFileDownloadResult {
  Data: {
    Content: string;
    ContentType: string;
  };
}

interface PerDiemFetchResult {
  Data: {
    ExistingDocs: PerDiemFile[];
    UnexpiredDocs: PerDiemFile[];
  };
}

interface UploadedPerDiemFileResult {
  Name: string;
  AttachmentID: number;
  KeyId: number;
  FileID: string;
  ServiceDate: Date;
  ExpirationDate: Date;
  PerDiemFileID: string;
}

@Injectable({
  providedIn: 'root',
})
export class PerDiemService {
  showMessage$: Subject<{ type: string; message: string }> = new Subject();
  protected spartaClient: HttpClient;
  protected vpClient: HttpClient;

  constructor(private clientFactory: MyHttpClientFactory) {
    this.spartaClient = this.clientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
    this.vpClient = this.clientFactory.createHttpClient(
      environment.urls.viewpointAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
  }

  /** Facility per diem rule for a job's plant; null when the job has no plant or the plant has no rule. */
  public fetchPerDiemRuleForJob(
    manPowerReqID: string
  ): Promise<PerDiemRuleDto | null> {
    const url = GET_PER_DIEM_RULE_FOR_JOB.replace(
      '{manPowerReqID}',
      manPowerReqID
    );
    return this.spartaClient
      .get<PerDiemRuleDto | null>(url)
      .toPromise()
      .then(parseData);
  }

  /** Facility per diem rule for an employee slot's job plant (recruiter/dispatch record); null when unresolvable. */
  public fetchPerDiemRuleForDetail(
    manPowerReqDetailID: string
  ): Promise<PerDiemRuleDto | null> {
    const url = GET_PER_DIEM_RULE_FOR_DETAIL.replace(
      '{manPowerReqDetailID}',
      manPowerReqDetailID
    );
    return this.spartaClient
      .get<PerDiemRuleDto | null>(url)
      .toPromise()
      .then(parseData);
  }

  public downloadFileFromSparta(
    fileId: string | number
  ): Promise<SpartaFileLinkResult> {
    const url = DOWNLOAD_FROM_SPARTA + String(fileId);
    return this.spartaClient
      .get<SpartaFileLinkResult>(url)
      .toPromise()
      .then(parseData, (error) => {
        throw error;
      });
  }

  public downloadFileFromViewPoint(
    attachmentId: number
  ): Promise<ViewpointFileDownloadResult> {
    const url = DOWNLOAD_FROM_VIEWPOINT + attachmentId;
    return this.spartaClient
      .get<ViewpointFileDownloadResult>(url)
      .toPromise()
      .then(parseData, (error) => {
        throw error;
      });
  }

  public fetch(
    manPowerReqDetailID: string,
    employeeId: string,
    jobId: string,
    isToGetExpiredData: boolean
  ): Promise<PerDiemFetchResult> {
    return this.fetchExtended(
      manPowerReqDetailID,
      null,
      employeeId,
      jobId,
      isToGetExpiredData
    );
  }

  public fetchExtended(
    manPowerReqDetailID: string,
    employeeJobID: string,
    employeeId: string,
    jobId: string,
    isToGetExpiredData: boolean
  ): Promise<PerDiemFetchResult> {
    let url: string;
    if (isToGetExpiredData === null) {
      url = GET_ALL_PER_DIEM + employeeId;
    } else if (isToGetExpiredData) {
      url = GET_EXPIRED_PER_DIEM + employeeId;
    } else {
      url = GET_UNEXPIRED_PER_DIEM + employeeId;
    }
    if (jobId)
      url =
        url + '/' + jobId + '?exceptManPowerReqDetailID=' + manPowerReqDetailID;
    if (jobId && employeeJobID)
      url = url + '&exceptEmployeeJobID=' + employeeJobID;
    return this.spartaClient
      .get<PerDiemFetchResult>(url)
      .toPromise()
      .then(parseData);
  }

  public fetchSupportedTypes(): Promise<unknown[]> {
    const url = GET_PER_DIEM_SUPPORTED_TYPES;
    return this.vpClient.get<unknown[]>(url).toPromise().then(parseData);
  }

  public uploadFile(
    file: File,
    perDiemFile: PerDiemFile,
    employeeId: string,
    jobId: string,
    manpowerReqDetailId: string
  ): Promise<UploadedPerDiemFileResult> {
    // A missing file must fail as a REJECTED PROMISE, not a synchronous throw.
    // Callers (saveHandler) attach .then().catch() AFTER invoking this; a
    // synchronous throw here (formData.append(..., file.name) on an undefined
    // file) escapes that .catch entirely, so the add fails silently with no
    // request and no user feedback (#29586 second failure mode).
    if (!file) {
      return Promise.reject({
        Messages: ['Please choose a file before saving the document.'],
      });
    }
    let url =
      SAVE_PER_DIEM_FILE +
      employeeId +
      '/' +
      perDiemFile.DocType +
      this.dressDownDate(perDiemFile.SrvDate) +
      this.dressDownDate(perDiemFile.ExpDate) +
      '/';
    if (jobId && manpowerReqDetailId) {
      url = url + manpowerReqDetailId + '/' + jobId;
    } else {
      url = url + '00000000-0000-0000-0000-000000000000/0';
    }
    const formData = new FormData();
    formData.append('uploadFile', file, file.name);
    const headers = new HttpHeaders().append('Accept', 'application/json');
    const options = { headers };
    return this.spartaClient
      .post<UploadedPerDiemFileResult>(url, formData, options)
      .toPromise()
      .then(parseData);
  }

  public updateFile(
    perDiemFile: PerDiemFile,
    employeeId: string,
    manpowerReqDetailId: string,
    jobId: string
  ) {
    if (employeeId && employeeId !== '00000000-0000-0000-0000-000000000000') {
      const promises: Array<Promise<unknown>> = [];
      if (perDiemFile.VPKeyID) {
        const url = UPDATE_PER_DIEM_VIEWPOINT;
        const perDiemViewpointSideUpdate: PerDiemViewpointSideUpdate =
          new PerDiemViewpointSideUpdate();
        perDiemViewpointSideUpdate.KeyId = perDiemFile.VPKeyID;
        perDiemViewpointSideUpdate.DocType = perDiemFile.DocType;
        perDiemViewpointSideUpdate.Notes = perDiemFile.Notes;
        perDiemViewpointSideUpdate.ServiceDate = perDiemFile.SrvDate;
        perDiemViewpointSideUpdate.ExpirationDate = perDiemFile.ExpDate;
        const body = JSON.stringify(perDiemViewpointSideUpdate);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        promises.push(
          this.vpClient
            .put(url, body, options)
            .toPromise()
            .catch((e) => {
              this.showMessage$.next({ type: 'error', message: e.Messages });
              console.error(e);
            })
        );
      }
      if (
        perDiemFile.FileID &&
        perDiemFile.FileID !== '00000000-0000-0000-0000-000000000000'
      ) {
        const url = UPDATE_PER_DIEM_SPARTA;
        const perDiemSpartaSideUpdate: PerDiemSpartaSideUpdate =
          new PerDiemSpartaSideUpdate();
        perDiemSpartaSideUpdate.FileID = perDiemFile.FileID;
        perDiemSpartaSideUpdate.Name = perDiemFile.FileName;
        perDiemSpartaSideUpdate.ServiceDate = perDiemFile.SrvDate;
        perDiemSpartaSideUpdate.ExpirationDate = perDiemFile.ExpDate;
        perDiemSpartaSideUpdate.AttachmentID = perDiemFile.VPAttachmentID;
        perDiemSpartaSideUpdate.Notes = perDiemFile.Notes;
        perDiemSpartaSideUpdate.DocType = perDiemFile.DocType;
        perDiemSpartaSideUpdate.KeyId = perDiemFile.VPKeyID;
        const body = JSON.stringify(perDiemSpartaSideUpdate);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        promises.push(
          this.spartaClient
            .put(url, body, options)
            .toPromise()
            .catch((e) => {
              this.showMessage$.next({ type: 'error', message: e.Messages });
              console.error(e);
            })
        );
      } else {
        promises.push(
          this.createSpartaSideRecordWithPerDiemFileAssociation(
            perDiemFile,
            employeeId,
            manpowerReqDetailId,
            jobId
          ).then(
            (data) => {
              perDiemFile.FileID = data.FileID;
              perDiemFile.PerDiemFileID = data.PerDiemFileID;
            },
            (error) => {
              // Bug 31922: surface the real message; the batch still continues.
              console.error(error);
              this.showMessage$.next({
                type: 'error',
                message: apiErrorMessage(error, 'Could not save the document.'),
              });
            }
          )
        );
      }

      return forkJoin(promises).toPromise();
    }
  }

  public deleteFile(
    perDiemFile: PerDiemFile,
    employeeId: string,
    manpowerReqDetailId: string,
    jobId: string
  ): void {
    let url =
      ALTER_PER_DIEM_ASSOCIATION + perDiemFile.PerDiemFileID + '/' + employeeId;
    if (jobId && manpowerReqDetailId)
      url = url + '/' + jobId + '/' + manpowerReqDetailId;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers };
    this.spartaClient.delete(url, options).toPromise();
  }

  public deleteFileWithReturnValue(
    perDiemFile: PerDiemFile,
    employeeId: string,
    manpowerReqDetailId: string,
    jobId: string
  ) {
    let url =
      ALTER_PER_DIEM_ASSOCIATION + perDiemFile.PerDiemFileID + '/' + employeeId;
    if (jobId && manpowerReqDetailId)
      url = url + '/' + jobId + '/' + manpowerReqDetailId;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers };
    return this.spartaClient.delete(url, options).toPromise();
  }

  public addFile(
    perDiemFile: PerDiemFile,
    employeeId: string,
    manpowerReqDetailId: string,
    jobId: string
  ): Promise<unknown> {
    const url =
      ALTER_PER_DIEM_ASSOCIATION +
      perDiemFile.PerDiemFileID +
      '/' +
      employeeId +
      '/' +
      jobId +
      '/' +
      manpowerReqDetailId;
    const formData = new FormData();
    const headers = new HttpHeaders().append('Accept', 'application/json');
    const options = { headers };
    return this.spartaClient.post(url, formData, options).toPromise();
  }

  public createSpartaSideRecordWithPerDiemFileAssociation(
    perDiemFile: PerDiemFile,
    employeeId: string,
    manpowerReqDetailId: string,
    jobId: string
  ): Promise<PerDiemAssociationResult> {
    let url =
      CREATE_SPARTA_SIDE_RECORD_WITH_PER_DIEM_FILE_ASSOCIATION + employeeId;
    if (jobId && manpowerReqDetailId)
      url = url + '/' + jobId + '/' + manpowerReqDetailId;
    // Bug 31922: reject on failure (no swallowing .catch) so each caller's own
    // error handler surfaces the message.
    return this.postSpartaSideRecord(url, perDiemFile);
  }

  public createSpartaSideRecordWithoutPerDiemFileAssociation(
    perDiemFile: PerDiemFile,
    employeeId: string,
    manpowerReqDetailId: string,
    jobId: string
  ): Promise<PerDiemAssociationResult> {
    let url =
      CREATE_SPARTA_SIDE_RECORD_WITHOUT_PER_DIEM_FILE_ASSOCIATION + employeeId;
    if (jobId && manpowerReqDetailId)
      url = url + '/' + jobId + '/' + manpowerReqDetailId;
    return this.postSpartaSideRecord(url, perDiemFile);
  }

  public fetchPerdiemData(): Observable<unknown> {
    const url = GET_PERDIEM_DATA;
    return this.spartaClient
      .get(url)
      .pipe(map((response) => parseData(response)));
  }

  public fetchAllPerdiemData(): Observable<unknown> {
    const url = GET_ALL_PERDIEM_DATA;
    return this.spartaClient
      .get(url)
      .pipe(map((response) => parseData(response)));
  }

  public fetchPerdiemAllJobsData(): Observable<unknown> {
    const url = GET_PERDIEM_ALL_JOBS_DATA;
    return this.spartaClient
      .get(url)
      .pipe(map((response) => parseData(response)));
  }

  public fetchPerdiemJobsData(): Observable<unknown> {
    const url = GET_PERDIEM_JOBS_DATA;
    return this.spartaClient
      .get(url)
      .pipe(map((response) => parseData(response)));
  }

  public fetchSelectedPerdiemJobsData(jobId: string): Observable<unknown> {
    const url = GET_SELECTED_PERDIEM_JOBS_DATA.replace('{jobId}', jobId);
    return this.spartaClient
      .get(url)
      .pipe(map((response) => parseData(response)));
  }

  public fetchSelectedPerdiemJobsOData(
    jobId: string,
    odatastring: string
  ): Observable<unknown> {
    let url = GET_SELECTED_PERDIEM_JOBS_ODATA.replace('{jobId}', jobId);

    url += '?$format=json&$count=true';
    url += odatastring ? '&' + odatastring : '';

    return this.spartaClient
      .get(url)
      .pipe(map((response) => parseData(response)));
  }

  public fetchPerdiemPopupData(
    employeeJobID: string | number
  ): Promise<unknown> {
    const url = GET_DATA_FOR_PERDIEM_POPUP.replace(
      '{employeeJobId}',
      String(employeeJobID)
    );

    return this.spartaClient.get(url).toPromise().then(parseData);
  }

  public fetchFileDataForPerdiemPopup(
    fileId: string | number
  ): Promise<unknown> {
    const url = GET_FILE_DATA_FOR_PERDIEM_POPUP.replace(
      '{fileID}',
      String(fileId)
    );

    return this.spartaClient.get(url).toPromise();
    // .then(this.parseData);
  }

  public acceptPerdiemFile(perDiemFileID: string | number): Promise<unknown> {
    const url = ACCEPT_PERDIEM_FILE.replace(
      '{perDiemFileID}',
      String(perDiemFileID)
    );

    return this.spartaClient.put(url, null).toPromise();
  }

  public rejectPerdiemFile(perDiemFileID: string | number): Promise<unknown> {
    const url = REJECT_PERDIEM_FILE.replace(
      '{perDiemFileID}',
      String(perDiemFileID)
    );

    return this.spartaClient.put(url, null).toPromise();
  }

  /** One consolidated PDF containing each employee's 3-page packet (PerDiemPackets-{jobId}.pdf). */
  public generatePerDiemPacketsForJob(
    jobId: string
  ): Observable<HttpResponse<Blob>> {
    const url = GENERATE_PERDIEM_PACKETS_FOR_JOB.replace(
      '{jobId}',
      String(jobId)
    );

    return this.spartaClient.post(url, null, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  /** Packet(s) for specific employees: application/pdf for one, a zip for several. */
  public generatePerDiemPacketsForEmployees(
    employeeJobIds: string[],
    jobId: string
  ): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    formData.append('employeeJobIds', employeeJobIds.join(','));
    formData.append('jobId', String(jobId));

    return this.spartaClient.post(
      GENERATE_PERDIEM_PACKETS_FOR_EMPLOYEES,
      formData,
      {
        observe: 'response',
        responseType: 'blob',
      }
    );
  }

  // Builds the Sparta-side update DTO and POSTs it, resolving the association
  // result or rejecting on failure. firstValueFrom replaces the deprecated
  // .toPromise().
  private postSpartaSideRecord(
    url: string,
    perDiemFile: PerDiemFile
  ): Promise<PerDiemAssociationResult> {
    const sideUpdate: PerDiemSpartaSideUpdate = new PerDiemSpartaSideUpdate();
    sideUpdate.Name = perDiemFile.FileName;
    sideUpdate.ServiceDate = perDiemFile.SrvDate;
    sideUpdate.ExpirationDate = perDiemFile.ExpDate;
    sideUpdate.AttachmentID = perDiemFile.VPAttachmentID;
    sideUpdate.Notes = perDiemFile.Notes;
    sideUpdate.DocType = perDiemFile.DocType;
    sideUpdate.KeyId = perDiemFile.VPKeyID;
    return firstValueFrom(
      this.spartaClient.post<PerDiemAssociationResult>(url, sideUpdate)
    ).then(parseData);
  }

  private dressDownDate(date: Date) {
    return (
      '/' +
      date.getFullYear() +
      '/' +
      (date.getMonth() + 1) +
      '/' +
      date.getDate()
    );
  }
}
